// src/pages/proyectos/components/MiembrosList.tsx
import { useState } from 'react';
import { Button, Spinner, Avatar } from '@/components/ui';
import { Plus, MoreVertical, UserMinus } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { useMiembros, useRemoverMiembro, useActualizarRolMiembro } from '@/hooks/queries/useProyectos';
import { MiembroFormModal } from './MiembroFormModal';
import { ROL_PROYECTO_LABELS } from '@/types';
import type { ProyectoMiembro, RolProyecto } from '@/types';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/Dropdown';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';

interface Props {
  proyectoId: string;
}

export function MiembrosList({ proyectoId }: Props) {
  const { data: miembros, isLoading } = useMiembros(proyectoId);
  const removerMutation = useRemoverMiembro();
  const actualizarRolMutation = useActualizarRolMiembro();
  const [modalOpen, setModalOpen] = useState(false);

  const handleRemove = async (miembro: ProyectoMiembro) => {
    if (confirm(`¿Remover a ${miembro.usuario.nombre} del proyecto?`)) {
      await removerMutation.mutateAsync({
        proyectoId,
        usuarioId: miembro.usuario_id,
      });
    }
  };

  const handleChangeRol = async (miembro: ProyectoMiembro, rol: RolProyecto) => {
    await actualizarRolMutation.mutateAsync({
      proyectoId,
      usuarioId: miembro.usuario_id,
      data: { rol },
    });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-medium">Miembros del equipo</h3>
        <Button size="sm" onClick={() => setModalOpen(true)}>
          <Plus className="h-4 w-4 mr-1" />
          Agregar Miembro
        </Button>
      </div>

      {!miembros || miembros.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          No hay miembros asignados. Agrega el primer miembro del equipo.
        </div>
      ) : (
        <div className="space-y-3">
          {miembros.map((miembro) => (
            <div
              key={miembro.usuario_id}
              className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg"
            >
              <Avatar
                fallback={miembro.usuario.nombre}
                size="md"
              />

              <div className="flex-1 min-w-0">
                <div className="font-medium">{miembro.usuario.nombre}</div>
                {miembro.usuario.email && (
                  <div className="text-sm text-gray-500">{miembro.usuario.email}</div>
                )}
                <div className="text-xs text-gray-400 mt-1">
                  Agregado: {format(new Date(miembro.fecha_asignacion), "dd MMM yyyy", { locale: es })}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Select
                  value={miembro.rol}
                  onValueChange={(value) => handleChangeRol(miembro, value as RolProyecto)}
                >
                  <SelectTrigger className="w-36">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(ROL_PROYECTO_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={() => handleRemove(miembro)}
                      className="text-red-600"
                    >
                      <UserMinus className="h-4 w-4 mr-2" />
                      Remover del proyecto
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          ))}
        </div>
      )}

      <MiembroFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        proyectoId={proyectoId}
        miembrosActuales={miembros?.map(m => m.usuario_id) ?? []}
      />
    </div>
  );
}
